// src/components/FloorPlanGenerator/AIModal.js
import React, { useState } from 'react';
import { useGroqKey } from '../../hooks/useGroqKey';
import { getGroqFloorPlanService } from '../../services/groqFloorPlanService';
import { elementTypes } from '../../constants/elementTypes';
import AIKeyInput from './AIKeyInput';
import RawResponseDisplay from './RawResponseDisplay';
import './AIModal.css';

const DEFAULT_PLOT_SIZE = { width: 1000, height: 800 };

const AIModal = ({ onClose, onGenerate }) => {
  const { apiKey, isKeySet, setKey } = useGroqKey();
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [rawResponse, setRawResponse] = useState(null);
  const [requirements, setRequirements] = useState({
    plotSize: DEFAULT_PLOT_SIZE,
    zoneTypes: [],
    specificRequirements: ''
  });

  // Group zone types by category for better organization
  const groupedZoneTypes = elementTypes.reduce((groups, type) => {
    const category = type.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(type.name);
    return groups;
  }, {});

  const validateRequirements = () => {
    if (!isKeySet) {
      throw new Error('API key is required');
    }
    if (!requirements.zoneTypes.length) {
      throw new Error('Please select at least one zone type');
    }
    if (!requirements.plotSize.width || !requirements.plotSize.height) {
      throw new Error('Please enter valid plot dimensions');
    }
  };

  const handleGenerate = async () => {
    try {
      validateRequirements();
      setIsGenerating(true);
      setError(null);

      const service = getGroqFloorPlanService(apiKey);
      const { elements, rawResponse: response } = await service.generateFloorPlan(requirements);
      setRawResponse(response);
      
      if (elements) {
        onGenerate(elements);
      }
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message);
      if (err.rawResponse) {
        setRawResponse(err.rawResponse);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePlotSizeChange = (dimension, value) => {
    setRequirements(prev => ({
      ...prev,
      plotSize: {
        ...prev.plotSize,
        [dimension]: Number(value) || 0
      }
    }));
  };

  const handleZoneTypeToggle = (type) => {
    setRequirements(prev => ({
      ...prev,
      zoneTypes: prev.zoneTypes.includes(type)
        ? prev.zoneTypes.filter(t => t !== type)
        : [...prev.zoneTypes, type]
    }));
  };

  if (!isKeySet) {
    return (
      <div className="modal">
        <div className="modal-content">
          <h3>AI Floor Plan Generation</h3>
          <AIKeyInput onSubmit={setKey} error={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="modal">
      <div className="modal-content">
        <h3>AI Floor Plan Generation</h3>
        
        <div className="input-group">
          <label>Plot Size:</label>
          <div className="size-inputs">
            <input
              type="number"
              min="100"
              value={requirements.plotSize.width}
              onChange={(e) => handlePlotSizeChange('width', e.target.value)}
              placeholder="Width"
            />
            <input
              type="number"
              min="100"
              value={requirements.plotSize.height}
              onChange={(e) => handlePlotSizeChange('height', e.target.value)}
              placeholder="Height"
            />
          </div>
        </div>

        <div className="input-group">
          <label>Zone Types:</label>
          {Object.entries(groupedZoneTypes).map(([category, types]) => (
            <div key={category} className="zone-category">
              <h4 className="category-title">{category.charAt(0).toUpperCase() + category.slice(1)}s</h4>
              <div className="checkbox-group">
                {types.map(type => (
                  <label key={type} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={requirements.zoneTypes.includes(type)}
                      onChange={() => handleZoneTypeToggle(type)}
                    />
                    <span>{type}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="input-group">
          <label>Specific Requirements:</label>
          <textarea
            value={requirements.specificRequirements}
            onChange={(e) => setRequirements(prev => ({
              ...prev,
              specificRequirements: e.target.value
            }))}
            placeholder="Enter any specific requirements or constraints..."
            rows={4}
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {rawResponse && (
          <RawResponseDisplay response={rawResponse} />
        )}

        <div className="modal-actions">
          <button
            className="action-button"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate Plan'}
          </button>
          <button
            className="action-button cancel"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIModal;